import { useState } from 'react';
import {
    Box,
    Typography,
    Card,
    CardHeader,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Chip,
    Paper,
    useTheme,
    alpha,
    Avatar
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ArrowRight as ArrowIcon,
    Update as UpdateIcon,
    Book as BookIcon,
    AddCircle as AddCircleIcon,
    Person as PersonIcon,
    ChildCare as ChildCareIcon,
    Info as InfoIcon
} from '@mui/icons-material';

// Add last updated date
const lastUpdated = "2025-01-03";

const guides = [
    {
        title: "Changing a Client's Membership",
        icon: <UpdateIcon />,
        description: "How to upgrade or modify a client's existing membership.",
        steps: [
            {
                content: "Log into EZFacility and select the client you want to edit.",
                type: "step"
            },
            {
                content: "Scroll down to their \"Membership\" and click \"edit\".",
                type: "step"
            },
            {
                content: "Make note of their current membership (e.g., \"2 Players (2025)\")",
                type: "step"
            },
            {
                content: "Cancel their current membership and use today's date",
                type: "step"
            },
            {
                content: "Scroll up on the client's page and navigate to the memberships tab",
                type: "step"
            },
            {
                content: "Click \"New Contract\"",
                type: "step"
            },
            {
                content: "Select the desired membership (e.g., \"3 Players\" if upgrading from 2)",
                type: "step"
            },
            {
                content: "Change the price to $10.00, since they already paid for their existing membership",
                type: "step"
            },
            {
                content: "This process is typically used when upgrading a membership (e.g., from 2 Players to 3 Players)",
                type: "note"
            }
        ]
    },
    {
        title: "Managing Children for Clients",
        icon: <ChildCareIcon />,
        description: "Instructions for linking and managing children's accounts.",
        steps: [
            {
                content: "Navigate to the Clients tab in the admin panel",
                type: "step"
            },
            {
                content: "Use the search function to find the parent's account",
                type: "step"
            },
            {
                content: "Click on the parent's name to view their details",
                type: "step"
            },
            {
                content: "Click the 'Manage Children' button in their profile",
                type: "step"
            },
            {
                content: "To add a child: Click the '+' button and search for the child by name",
                type: "step"
            },
            {
                content: "If the child exists: Select them from the search results to link them to the parent",
                type: "substep"
            },
            {
                content: "If the child doesn't exist: The parent should create the child account through their portal",
                type: "substep"
            },
            {
                content: "To remove a child: Click the delete (trash) icon next to the child's name",
                type: "step"
            },
            {
                content: "Changes may take up to 1 minute to be reflected in both the parent and child accounts",
                type: "note"
            }
        ]
    },
    {
        title: "Adding a New Membership",
        icon: <AddCircleIcon />,
        description: "Learn how to create and configure new membership plans for your clients.",
        steps: [
            {
                content: "Otherwise known as \"Adding Administrative fees\". A membership is technically what we use to charge Administrative fees.",
                type: "note"
            },
            {
                content: "Navigate to Memberships > Membership Types",
                type: "step"
            },
            {
                content: "Create a new membership type if needed",
                type: "step"
            },
            {
                content: "Navigate to Memberships > Membership Plans",
                type: "step"
            },
            {
                content: "Create a new plan if needed",
                type: "step"
            },
            {
                content: "Make sure to check \"Sell Online\" if you want users to be able to purchase it",
                type: "step"
            }
        ]
    },
    {
        title: "New Client Setup",
        icon: <PersonIcon />,
        description: "Guide for onboarding new clients and getting them started.",
        steps: [
            {
                content: "Direct new client to self-service registration and have them complete the form",
                type: "step"
            },
            {
                content: "If they have an account already with the email and phone provided, it will send them an activation email for self-service rather than create another account.",
                type: "note"
            },
            {
                content: "Verify email confirmation is received",
                type: "step"
            },
            {
                content: "EZFacility will send the user a sign-up email. This is where they will create their username and password.",
                type: "note"
            },
            {
                content: "Ensure the user signs ups for a membership plan",
                type: "step"
            },
            {
                content: "Otherwise known as \"Paying Administrative fees\". They will check out through the self service site and return to this site.",
                type: "note"
            },
            {
                content: "Have the user show their Entry Pass to check in",
                type: "step"
            }
        ]
    }
];

const Guide = () => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const getStepNumber = (steps, currentIndex) => {
        let stepNumber = 0;
        for (let i = 0; i <= currentIndex; i++) {
            if (steps[i].type === 'step') {
                stepNumber++;
            }
        }
        return stepNumber;
    };

    return (
        <Box>
            <Paper 
                elevation={0}
                sx={{ 
                    p: 3, 
                    mb: 4, 
                    background: theme.palette.primary.main,
                    color: 'white',
                    borderRadius: 2
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <BookIcon sx={{ fontSize: 40 }} />
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        Admin Guide
                    </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    Comprehensive guides and instructions for managing Total Turf Experience memberships.
                </Typography>
                <Chip
                    icon={<UpdateIcon />}
                    label={`Last Updated: ${lastUpdated}`}
                    sx={{ 
                        mt: 2,
                        backgroundColor: alpha(theme.palette.common.white, 0.15),
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' }
                    }}
                    size="small"
                />
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {guides.map((guide, index) => (
                    <Card
                        key={index}
                        sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: theme.shadows[4]
                            }
                        }}
                    >
                        <Accordion
                            expanded={expanded === `panel${index}`}
                            onChange={handleChange(`panel${index}`)}
                            elevation={0}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2
                                    }
                                }}
                            >
                                <Box sx={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: theme.palette.primary.main
                                }}>
                                    {guide.icon}
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
                                        {guide.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {guide.description}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), p: 3 }}>
                                <List sx={{ 
                                    py: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2
                                }}>
                                    {guide.steps.map((step, stepIndex) => (
                                        <ListItem 
                                            key={stepIndex}
                                            sx={{ 
                                                p: 0,
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                ml: step.type === 'substep' ? 4 : 0
                                            }}
                                        >
                                            {step.type === 'step' && (
                                                <Avatar 
                                                    sx={{ 
                                                        width: 28,
                                                        height: 28,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                        color: theme.palette.primary.main,
                                                        fontSize: '0.875rem',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {getStepNumber(guide.steps, stepIndex)}
                                                </Avatar>
                                            )}
                                            {step.type === 'note' && (
                                                <InfoIcon 
                                                    sx={{ 
                                                        color: theme.palette.info.main,
                                                        opacity: 0.7,
                                                        fontSize: 24,
                                                        mt: 0.25
                                                    }}
                                                />
                                            )}
                                            {step.type === 'substep' && (
                                                <ArrowIcon 
                                                    sx={{ 
                                                        color: theme.palette.text.secondary,
                                                        opacity: 0.5,
                                                        fontSize: 20,
                                                        mt: 0.5
                                                    }}
                                                />
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography 
                                                    variant="body1"
                                                    sx={{ 
                                                        color: step.type === 'note' ? 'info.main' : 'text.primary',
                                                        lineHeight: 1.5,
                                                        mt: 0.25,
                                                        fontStyle: step.type === 'note' ? 'italic' : 'normal',
                                                        opacity: step.type === 'substep' ? 0.87 : 1
                                                    }}
                                                >
                                                    {step.content}
                                                </Typography>
                                            </Box>
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    </Card>
                ))}
            </Box>
        </Box>
    );
};

export default Guide;
